import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, ImageReveal, Chapter, ParallaxBand } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { IMG } from "../data/images";

const GAP_POINTS = [
  "Finding reliable factories",
  "Minimum order requirements",
  "Communication gaps",
  "Cost negotiations",
  "Product development",
  "Quality control",
  "Production follow-up",
  "Shipping timelines",
];

const SERVICES = [
  { n: "01", title: "Factory Sourcing", text: "Finding and evaluating suitable manufacturing partners based on product, quality, price, capacity and MOQ requirements." },
  { n: "02", title: "Product Development", text: "Turning product ideas, references and specifications into commercially viable products." },
  { n: "03", title: "Costing & Negotiation", text: "Building accurate costings and negotiating with suppliers to protect your margins." },
  { n: "04", title: "Production Management", text: "Managing timelines, approvals, communication and production follow-up." },
  { n: "05", title: "Quality Control", text: "Helping ensure products meet agreed specifications and quality expectations." },
  { n: "06", title: "Shipment Coordination", text: "Supporting the process from production completion through final shipment." },
];

const STEPS = [
  { n: "01", name: "Discover", text: "We take time to understand your brand, product, target customer and requirements — before anything else." },
  { n: "02", name: "Source", text: "We identify suppliers and manufacturing partners suited to your product, quantities and standards." },
  { n: "03", name: "Develop", text: "Samples, costing, materials, specifications and approvals — the idea becomes a real product." },
  { n: "04", name: "Produce", text: "Production follow-up, communication and quality oversight while your order is on the line." },
  { n: "05", name: "Ship", text: "Final coordination and shipment, from production completion to your warehouse door." },
];

const INDUSTRIES = [
  { name: "Footwear", img: IMG.footwearStudio, note: "Sneakers, leather shoes, sandals" },
  { name: "Bags & Accessories", img: IMG.bagsStudio, note: "Leather goods, totes, small accessories" },
  { name: "Apparel", img: IMG.apparelStack, note: "Cut-and-sew, knits, wovens" },
  { name: "Jewelry & Accessories", img: IMG.jewelryDetail, note: "Fine and fashion jewelry, metalwork" },
];

const PROOF = ["Product Development", "Vendor Sourcing", "Cost Negotiation", "Production Follow-Up", "Quality Control"];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.3]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-ivory pt-32 md:pt-40">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 pb-20 md:grid-cols-12 md:px-12 md:pb-28">
        <div className="md:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-label text-[11px] font-semibold uppercase tracking-[0.3em] text-stone-500"
          >
            Sourcing · Development · Production — India
          </motion.p>
          <h1 className="mt-8 font-serif text-[13vw] leading-[1.02] tracking-tight text-ink sm:text-6xl md:text-7xl lg:text-[5.2rem]">
            <MaskedLine delay={0.35}>Your partner</MaskedLine>
            <MaskedLine delay={0.47}>
              in <span className="italic text-terra">India,</span>
            </MaskedLine>
            <MaskedLine delay={0.59}>from first sample</MaskedLine>
            <MaskedLine delay={0.71}>to final shipment.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg"
          >
            We help growing consumer brands find the right manufacturing partners, develop products, manage
            production and protect quality — without the cost of building a local sourcing office.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-start"
              className="group inline-flex items-center gap-3 bg-terra px-8 py-4 font-label text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-terra-dark"
            >
              Let's Build Something
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <button
              data-testid="hero-cta-how"
              onClick={() => document.getElementById("gap")?.scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.2em] text-stone-600 transition-colors duration-300 hover:text-terra"
            >
              See How We Work
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
          </motion.div>
        </div>
        <div className="relative md:col-span-5">
          <motion.div style={{ y, opacity }} className="relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ delay: 0.5, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <motion.img
                src={IMG.hero}
                alt="Craftsman's workbench with leather tools and materials"
                className="aspect-[4/5] w-full object-cover"
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
              />
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              className="mt-4 font-label text-[10px] uppercase tracking-[0.26em] text-stone-500"
            >
              The workbench — where every product begins
            </motion.p>
          </motion.div>
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 1.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -left-8 top-10 hidden h-px w-16 origin-left bg-terra md:block"
          />
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="border-t border-stone-200"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-5 md:px-12">
          <p className="font-label text-[10px] uppercase tracking-[0.26em] text-stone-500" data-testid="hero-category-strip">
            Footwear / Bags / Apparel / Jewelry
          </p>
          <p className="font-label text-[10px] uppercase tracking-[0.26em] text-stone-500">
            Based in India — working with brands worldwide
          </p>
        </div>
      </motion.div>
    </section>
  );
};

const Gap = () => (
  <section id="gap" data-testid="gap-section" className="bg-ivory py-24 md:py-36">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <Chapter number="01" label="The Gap" />
      <div className="mt-12 grid gap-16 md:grid-cols-12">
        <div className="md:col-span-7">
          <Reveal>
            <h2 className="font-serif text-4xl leading-[1.1] tracking-tight text-ink md:text-5xl lg:text-6xl">
              You don't need a sourcing office. You need someone who knows how to{" "}
              <span className="italic text-terra">navigate India.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg">
              Growing brands run into the same walls when they look East for manufacturing. Not because India
              lacks capability — but because the middle ground between brand and factory is complicated,
              relationship-driven and hard to manage from another timezone.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-10 font-serif text-2xl italic text-stone-800">
              That's where Origin Bureau comes in.
            </p>
          </Reveal>
        </div>
        <div className="md:col-span-5">
          <Reveal delay={0.1}>
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
              The friction we remove
            </p>
          </Reveal>
          <ul className="mt-6 divide-y divide-stone-200 border-y border-stone-200">
            {GAP_POINTS.map((p, i) => (
              <Reveal key={p} delay={0.05 * i} y={16}>
                <li className="group flex items-center justify-between py-4">
                  <span className="text-sm text-stone-700 transition-colors duration-300 group-hover:text-terra md:text-base">{p}</span>
                  <span className="font-serif text-sm italic text-stone-300 transition-colors duration-300 group-hover:text-terra">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const SmallMoq = () => (
  <section data-testid="small-moq-section" className="noise-overlay relative bg-ink py-24 text-ivory md:py-40">
    <img
      src={IMG.production}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.09]"
    />
    <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
      <Chapter number="02" label="Small MOQ Advantage" dark />
      <div className="mt-14 grid gap-16 md:grid-cols-12">
        <div className="md:col-span-5">
          <Reveal>
            <h2 className="font-serif text-4xl leading-[1.12] tracking-tight md:text-5xl">
              Not ordering thousands? <span className="italic text-stone-400">That's okay.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-base leading-relaxed text-stone-400 md:text-lg">
              We work with growing brands that need flexibility — not factories that only care about volume.
              Whether you're launching a first collection, testing a product or producing a limited run, we
              actively look for manufacturing partners appropriate to your required quantities.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-stone-500">
              Not every factory accepts every MOQ — that's exactly why the search matters. Smaller MOQ does
              not mean smaller standards.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <ul className="mt-10 space-y-4">
              {["Smaller MOQs.", "Smarter sourcing.", "The same attention to detail."].map((l) => (
                <li key={l} className="flex items-center gap-4 font-serif text-xl text-stone-300 md:text-2xl">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-terra" />
                  {l}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <div className="flex flex-col justify-center md:col-span-7 md:pl-10">
          <Reveal y={50}>
            <p className="font-serif text-[11vw] leading-[1.02] tracking-tight sm:text-6xl md:text-7xl lg:text-[5.4rem]">
              BIG SOURCING
              <br />
              SUPPORT.
            </p>
          </Reveal>
          <Reveal y={50} delay={0.15}>
            <p className="mt-2 font-serif text-[11vw] italic leading-[1.02] tracking-tight text-terra sm:text-6xl md:text-7xl lg:text-[5.4rem]">
              SMALL-BRAND
              <br />
              FRIENDLY.
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section data-testid="services-section" className="bg-ivory py-24 md:py-36">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <Chapter number="03" label="What We Do" />
      <div className="mt-12 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-6">
          <Reveal>
            <h2 className="font-serif text-4xl leading-[1.1] tracking-tight text-ink md:text-5xl lg:text-6xl">
              From idea to shipment, we stay <span className="italic text-terra">close to the product.</span>
            </h2>
          </Reveal>
        </div>
        <div className="flex items-end md:col-span-6 md:justify-end">
          <Reveal delay={0.15}>
            <Link
              to="/services"
              data-testid="services-explore-link"
              className="group inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.22em] text-stone-600 transition-colors duration-300 hover:text-terra"
            >
              Explore all services
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </div>
      <div className="mt-16 grid border-t border-l border-stone-200 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.n} delay={0.06 * i} y={24}>
            <Link
              to="/services"
              data-testid={`service-card-${s.n}`}
              className="group flex h-full flex-col justify-between border-b border-r border-stone-200 p-8 transition-colors duration-500 hover:bg-stone-100 md:p-10"
            >
              <div>
                <span className="font-serif text-4xl font-light italic text-stone-300 transition-colors duration-500 group-hover:text-terra">
                  {s.n}
                </span>
                <h3 className="mt-6 font-serif text-2xl text-ink">{s.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-stone-600">{s.text}</p>
              </div>
              <ArrowRight className="mt-8 h-4 w-4 text-stone-300 transition-all duration-300 group-hover:translate-x-1 group-hover:text-terra" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Process = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 65%", "end 70%"] });
  return (
    <section data-testid="process-section" className="bg-stone-100 py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Chapter number="04" label="How It Works" />
        <div className="mt-12 grid gap-16 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32">
              <Reveal>
                <h2 className="font-serif text-4xl leading-[1.1] tracking-tight text-ink md:text-5xl">
                  Five steps. <br />
                  One continuous <span className="italic text-terra">journey.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-md text-base leading-relaxed text-stone-600">
                  You bring the vision. We navigate the supply chain — keeping you close to the decisions
                  without burying you in the day-to-day.
                </p>
              </Reveal>
              <ImageReveal src={IMG.development} alt="Hands developing a product sample with hand tools" className="mt-10 hidden aspect-[4/3] md:block" />
            </div>
          </div>
          <div ref={ref} className="relative md:col-span-7">
            <span className="absolute left-[27px] top-2 bottom-2 w-px bg-stone-300 md:left-[35px]" />
            <motion.span
              style={{ scaleY: scrollYProgress }}
              className="absolute left-[27px] top-2 bottom-2 w-px origin-top bg-terra md:left-[35px]"
            />
            <div className="space-y-14 md:space-y-20">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={0.05 * i}>
                  <div className="relative flex gap-8 pl-2 md:gap-12" data-testid={`process-step-${s.n}`}>
                    <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center border border-stone-300 bg-stone-100 font-serif text-lg italic text-terra md:h-[72px] md:w-[72px] md:text-2xl">
                      {s.n}
                    </span>
                    <div className="pt-1 md:pt-3">
                      <h3 className="font-label text-sm font-bold uppercase tracking-[0.3em] text-ink">{s.name}</h3>
                      <p className="mt-3 max-w-md text-sm leading-relaxed text-stone-600 md:text-base">{s.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Trust = () => (
  <section data-testid="trust-section" className="bg-ivory py-24 md:py-36">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <Chapter number="05" label="Why Brands Trust Us" />
      <div className="mt-12 grid items-start gap-16 md:grid-cols-12">
        <div className="md:col-span-7">
          <Reveal>
            <h2 className="font-serif text-4xl leading-[1.1] tracking-tight text-ink md:text-5xl lg:text-6xl">
              You get the local advantage without building the{" "}
              <span className="italic text-terra">local office.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg">
              Origin Bureau acts as the bridge between international brands and Indian manufacturing. We sit
              on your side of the table — understanding what your product needs to be, and what the factory
              needs to hear.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-wrap gap-3">
              {PROOF.map((p) => (
                <span
                  key={p}
                  className="border border-stone-300 px-5 py-2.5 font-label text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-600 transition-colors duration-300 hover:border-terra hover:text-terra"
                >
                  {p}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="md:col-span-5">
          <ImageReveal src={IMG.production} alt="Craftsman at work during the production phase" className="aspect-[4/5]" />
          <Reveal delay={0.2}>
            <p className="mt-4 font-label text-[10px] uppercase tracking-[0.26em] text-stone-500">
              On the ground — where timelines are kept
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const Philosophy = () => (
  <section data-testid="philosophy-section" className="border-y border-stone-200 bg-ivory py-24 md:py-36">
    <div className="mx-auto max-w-5xl px-6 text-left md:px-12">
      <Reveal>
        <p className="font-label text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">Why Origin Bureau</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-8 font-serif text-4xl leading-[1.12] tracking-tight text-ink md:text-6xl">
          India is full of possibilities. Finding the <span className="italic text-terra">right ones</span> is the work.
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-10 md:grid-cols-2">
        <Reveal delay={0.15}>
          <div className="space-y-5 border-l-2 border-terra pl-8">
            <p className="font-serif text-xl leading-relaxed text-stone-700 md:text-2xl">The right factory isn't always the biggest factory.</p>
            <p className="font-serif text-xl leading-relaxed text-stone-700 md:text-2xl">The right supplier isn't always the cheapest supplier.</p>
            <p className="font-serif text-xl leading-relaxed text-stone-700 md:text-2xl">And the right solution isn't always the one with the lowest MOQ.</p>
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="text-base leading-relaxed text-stone-600 md:text-lg">
            We focus on finding the right fit — because the right factory changes everything, and good
            products start with good sourcing.
          </p>
          <p className="mt-8 font-label text-xs font-bold uppercase tracking-[0.24em] leading-loose text-ink">
            Brand <span className="text-terra">×</span> Product <span className="text-terra">×</span> Factory{" "}
            <span className="text-terra">×</span> Quantity <span className="text-terra">×</span> Quality{" "}
            <span className="text-terra">×</span> Cost
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section data-testid="industries-section" className="bg-ivory py-24 md:py-36">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <Reveal>
          <div>
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">Who We Work With</p>
            <h2 className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight text-ink md:text-5xl lg:text-6xl">
              Products we know <span className="italic text-terra">by hand.</span>
            </h2>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="max-w-sm text-sm leading-relaxed text-stone-600">
            Four categories, one standard: tactile, well-made products built with the right materials and the
            right makers.
          </p>
        </Reveal>
      </div>
      <div className="mt-16 grid gap-x-10 gap-y-16 md:grid-cols-2 md:gap-y-24">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind.name} delay={0.07 * i}>
            <div className={i % 2 === 1 ? "md:translate-y-16" : ""} data-testid={`industry-${ind.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="group cursor-pointer">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={ind.img}
                    alt={`${ind.name} craftsmanship`}
                    loading="lazy"
                    className="h-full w-full object-cover grayscale transition-[filter,transform] duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06] group-hover:grayscale-0"
                  />
                </div>
                <div className="mt-5 flex items-baseline justify-between border-b border-stone-200 pb-4 transition-transform duration-500 ease-out group-hover:translate-x-2">
                  <div>
                    <h3 className="font-serif text-2xl text-ink transition-colors duration-300 group-hover:text-terra md:text-3xl">{ind.name}</h3>
                    <p className="mt-1 text-xs tracking-wide text-stone-500">{ind.note}</p>
                  </div>
                  <span className="font-serif text-sm italic text-stone-400 transition-colors duration-300 group-hover:text-terra">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const India = () => (
  <section data-testid="india-section" className="noise-overlay bg-ink py-24 text-ivory md:py-36">
    <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-16 px-6 md:grid-cols-12 md:px-12">
      <div className="md:col-span-6">
        <Chapter number="06" label="The India Connection" dark />
        <Reveal delay={0.1}>
          <h2 className="mt-10 font-serif text-4xl leading-[1.1] tracking-tight md:text-5xl lg:text-6xl">
            Made in India. Managed with <span className="italic text-terra-light">intention.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-lg text-base leading-relaxed text-stone-400 md:text-lg">
            India is our network. Your product is our focus. Behind every collection is an ecosystem of
            workshops, materials and people — we move inside it every day, so you don't have to figure it
            out alone.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            {["Craft", "Materials", "Workshops", "People", "Production", "Movement"].map((w) => (
              <span key={w} className="font-label text-[11px] font-semibold uppercase tracking-[0.26em] text-stone-500">
                {w}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
      <div className="md:col-span-6">
        <ImageReveal src={IMG.apparelRail} alt="Garments hanging on a rail in a workshop, warm neutral tones" className="aspect-[4/3]" />
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section data-testid="final-cta-section" className="bg-ivory py-28 md:py-44">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <Reveal>
        <p className="font-label text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">Start Here</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-8 max-w-5xl font-serif text-[11vw] leading-[1.05] tracking-tight text-ink sm:text-6xl md:text-7xl lg:text-[5.4rem]">
          Thinking about sourcing from India?{" "}
          <span className="italic text-terra">Let's start with the product.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-10 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg">
          Whether you're developing your first collection or looking for a better manufacturing partner,
          tell us what you're building.
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-12 flex flex-wrap items-center gap-8">
          <Link
            to="/contact"
            data-testid="final-cta-button"
            className="group inline-flex items-center gap-3 bg-terra px-10 py-5 font-label text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-terra-dark"
          >
            Start a Project
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href="mailto:nupur@obofficial.in"
            data-testid="final-cta-email"
            className="link-line font-label text-xs font-semibold uppercase tracking-[0.2em] text-stone-600 transition-colors duration-300 hover:text-terra"
          >
            nupur@obofficial.in
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <Gap />
      <ParallaxBand
        src={IMG.textiles}
        alt="Rolls of fabric and raw materials in warm natural light"
        caption="Materials, workshops, possibility — the ecosystem we move in every day"
      />
      <SmallMoq />
      <ServicesPreview />
      <Process />
      <Trust />
      <Philosophy />
      <Industries />
      <India />
      <Marquee />
      <FinalCta />
    </>
  );
}
